'use client';

/**
 * Photoslop image picker panel.
 *
 * Layout: two-column — left sidebar (brands + library categories) and a right
 * square-thumbnail grid. Section assets are loaded on-demand and kept in a
 * module-level cache so re-opening the panel never re-fetches.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../../config';
import { retroAuthFetch } from '../../utils/retroAuthFetch';

// ── Types (mirror server shapes) ──────────────────────────────────────────

export interface LibraryItem {
  filename: string;
  path: string;
  mtime: number;
}

export interface LibraryResponse {
  photoslop: LibraryItem[];
  uploads: LibraryItem[];
  generated: LibraryItem[];
  exports: LibraryItem[];
  counts: Record<'photoslop' | 'uploads' | 'generated' | 'exports', number>;
}

export type LibraryTab = 'photoslop' | 'uploads' | 'generated' | 'exports';

interface BrandAsset {
  filename: string;
  path: string;
  mtime: number;
  variant: string;
}

interface BrandFolder {
  key: string;
  label: string;
  count: number;
  cover?: string;
}

interface BrandsResponse {
  folders: BrandFolder[];
  assetsByFolder: Record<string, BrandAsset[]>;
}

type AnyAsset = LibraryItem | BrandAsset;

interface Section {
  key: string;
  label: string;
  count: number;
  cover?: string;
  type: 'brand' | 'library';
}

// ── Module-level cache — survives panel close/re-open ─────────────────────

let _brandsResp: BrandsResponse | null = null;
let _libraryResp: LibraryResponse | null = null;
const _assetCache = new Map<string, AnyAsset[]>();
let _cacheTs = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function isCacheFresh() {
  return _cacheTs > 0 && Date.now() - _cacheTs < CACHE_TTL_MS;
}

function computeRecentItems(library: LibraryResponse): LibraryItem[] {
  return [...(library.generated ?? []), ...(library.photoslop ?? []), ...(library.uploads ?? [])]
    .sort((a, b) => (b.mtime ?? 0) - (a.mtime ?? 0))
    .slice(0, 30);
}

/** Clears in-memory library + brand lists so the next panel load or reload fetches from the server. */
export function invalidatePhotoslopLibraryCache() {
  _brandsResp = null;
  _libraryResp = null;
  _assetCache.clear();
  _cacheTs = 0;
}

// ── Component ─────────────────────────────────────────────────────────────

interface PhotoslopLibraryPanelProps {
  onPick: (path: string) => void;
  /** When true, refresh library on mount */
  active?: boolean;
  /** Show upload button at the bottom */
  showUploadButton?: boolean;
}

export function PhotoslopLibraryPanel({
  onPick,
  active = true,
  showUploadButton = true,
}: PhotoslopLibraryPanelProps) {
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [assets, setAssets] = useState<AnyAsset[]>([]);
  const [search, setSearch] = useState('');
  const [loadingNav, setLoadingNav] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  // Build section list and prime the asset cache from the two API responses.
  const buildSections = useCallback((brands: BrandsResponse, library: LibraryResponse) => {
    // Pre-seed asset cache from whatever the APIs returned.
    for (const [k, v] of Object.entries(brands.assetsByFolder ?? {})) {
      if (Array.isArray(v)) _assetCache.set(k, v);
    }
    if (library.generated) _assetCache.set('generated', library.generated);
    if (library.photoslop) _assetCache.set('photoslop', library.photoslop);
    if (library.uploads) _assetCache.set('uploads', library.uploads);
    if (library.exports) _assetCache.set('exports', library.exports);

    const recentItems = computeRecentItems(library);
    _assetCache.set('recent', recentItems);

    const sects: Section[] = [
      {
        key: 'recent',
        label: 'Recent',
        count: recentItems.length,
        type: 'library' as const,
      },
      ...(brands.folders ?? []).map((f) => ({
        key: f.key,
        label: f.label,
        count: f.count,
        cover: f.cover,
        type: 'brand' as const,
      })),
      {
        key: 'generated',
        label: 'Generated',
        count: library.counts?.generated ?? 0,
        type: 'library' as const,
      },
      {
        key: 'photoslop',
        label: 'Photoslop',
        count: library.counts?.photoslop ?? 0,
        type: 'library' as const,
      },
      {
        key: 'uploads',
        label: 'Uploads',
        count: library.counts?.uploads ?? 0,
        type: 'library' as const,
      },
    ];
    return sects;
  }, []);

  // Initial load — use module cache if fresh, otherwise fetch.
  useEffect(() => {
    if (!active) return;

    if (isCacheFresh() && _brandsResp && _libraryResp) {
      const sects = buildSections(_brandsResp, _libraryResp);
      setSections(sects);
      if (sects.length > 0) setSelectedKey((k) => k ?? sects[0].key);
      return;
    }

    setLoadingNav(true);
    Promise.all([
      fetch(`${API_URL}/api/brand-refs`, { cache: 'no-store' }).then((r) =>
        r.ok ? r.json() : { folders: [], assetsByFolder: {} },
      ),
      retroAuthFetch('/api/brand-assets/library', { cache: 'no-store' }).then(async (r) => {
        if (!r.ok) return { photoslop: [], uploads: [], generated: [], exports: [], counts: {} };
        const data = await r.json();
        const gen = data.generated ?? [];
        return {
          photoslop: [],
          uploads: [],
          generated: gen.map((g) => ({ filename: g.filename, path: g.path, mtime: g.mtime })),
          exports: [],
          counts: { generated: gen.length, photoslop: 0, uploads: 0, exports: 0 },
        };
      }),
    ])
      .then(([brands, library]) => {
        _brandsResp = brands as BrandsResponse;
        _libraryResp = library as LibraryResponse;
        _cacheTs = Date.now();
        const sects = buildSections(_brandsResp, _libraryResp);
        setSections(sects);
        setSelectedKey((k) => k ?? sects[0]?.key ?? null);
      })
      .finally(() => setLoadingNav(false));
  }, [active, buildSections, reloadTick]);

  // Load assets for selected section (from cache when possible).
  useEffect(() => {
    if (!selectedKey) return;

    if (_assetCache.has(selectedKey)) {
      setAssets(_assetCache.get(selectedKey)!);
      return;
    }

    setLoadingAssets(true);

    if (selectedKey.endsWith('-ref')) {
      setAssets(_assetCache.get(selectedKey) ?? []);
      setLoadingAssets(false);
      return;
    }

    retroAuthFetch('/api/brand-assets/library', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : {}))
      .then((raw) => {
        const data = raw as { generated?: { filename: string; path: string; mtime?: number }[] };
        const library = {
          photoslop: [],
          uploads: [],
          generated: (data.generated ?? []).map((g) => ({
            filename: g.filename,
            path: g.path,
            mtime: g.mtime,
          })),
          exports: [],
          counts: { generated: data.generated?.length ?? 0 },
        } as LibraryResponse;
        let items;
        if (selectedKey === 'recent') {
          items = computeRecentItems(library);
        } else if (selectedKey === 'generated') {
          items = library.generated ?? [];
        } else {
          items = [];
        }
        _assetCache.set(selectedKey, items);
        setAssets(items);
      })
      .finally(() => setLoadingAssets(false));
  }, [selectedKey, reloadTick]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('spiceKey', selectedKey?.endsWith('-ref') ? selectedKey : 'custom-ref');
      const res = await retroAuthFetch('/api/spice/upload', { method: 'POST', body: fd });
      if (res.ok) {
        const data = (await res.json());
        if (data.path) {
          invalidatePhotoslopLibraryCache();
          const url = data.path.startsWith('http') ? data.path : `${API_URL}${data.path}`;
          onPick(url);
        }
      } else {
        alert(`Upload failed (${res.status})`);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const filtered = search.trim()
    ? assets.filter((a) => a.filename.toLowerCase().includes(search.toLowerCase()))
    : assets;

  const selectedSection = sections.find((s) => s.key === selectedKey);
  const brandSections = sections.filter((s) => s.type === 'brand');
  const libSections = sections.filter((s) => s.type === 'library' && s.key !== 'recent');

  const reloadFromServer = useCallback(() => {
    invalidatePhotoslopLibraryCache();
    setReloadTick((n) => n + 1);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      {/* Search + reload */}
      <div className="flex gap-1.5">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search imagery…"
          className="min-w-0 flex-1 rounded-lg border border-beige bg-cream-alt px-3 py-1.5 font-inter text-xs text-slate-800 placeholder:text-slate-400 focus:border-marine-green focus:bg-white focus:outline-none"
        />
        <button
          type="button"
          title="Reload library from disk"
          disabled={loadingNav || loadingAssets}
          onClick={reloadFromServer}
          className="shrink-0 rounded-lg border border-beige bg-white px-2.5 py-1.5 font-dank-mono text-[10px] uppercase tracking-widest text-slate-500 transition hover:border-marine-green hover:text-marine-green disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reload
        </button>
      </div>

      {loadingNav ? (
        <div className="py-12 text-center font-inter text-xs text-slate-400">Loading…</div>
      ) : (
        <div className="flex min-h-0 gap-3">
          {/* ── Left sidebar ───────────────────────────────────────── */}
          <div className="flex w-44 shrink-0 flex-col gap-0.5 overflow-y-auto pr-1">
            {/* Recent */}
            {sections.find((s) => s.key === 'recent') && (
              <>
                <button
                  type="button"
                  onClick={() => setSelectedKey('recent')}
                  className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 text-left transition ${
                    selectedKey === 'recent'
                      ? 'bg-marine-green text-white'
                      : 'text-slate-700 hover:bg-cream-alt'
                  }`}
                >
                  <span
                    className={`font-inter text-[11px] font-semibold ${
                      selectedKey === 'recent' ? 'text-white' : 'text-slate-800'
                    }`}
                  >
                    Recent
                  </span>
                  <span
                    className={`font-dank-mono text-[9px] ${
                      selectedKey === 'recent' ? 'text-white/70' : 'text-slate-400'
                    }`}
                  >
                    {sections.find((s) => s.key === 'recent')?.count ?? 0}
                  </span>
                </button>
                <div className="my-2 border-t border-beige" />
              </>
            )}

            {/* Brands */}
            {brandSections.length > 0 && (
              <>
                <p className="mb-1 px-1 font-dank-mono text-[9px] uppercase tracking-widest text-slate-400">
                  Brands
                </p>
                {brandSections.map((s) => (
                  <SidebarBrandItem
                    key={s.key}
                    section={s}
                    active={selectedKey === s.key}
                    onSelect={() => setSelectedKey(s.key)}
                  />
                ))}
                <div className="my-2 border-t border-beige" />
              </>
            )}

            {/* Library categories */}
            <p className="mb-1 px-1 font-dank-mono text-[9px] uppercase tracking-widest text-slate-400">
              Library
            </p>
            {libSections.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSelectedKey(s.key)}
                className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 text-left transition ${
                  selectedKey === s.key
                    ? 'bg-marine-green text-white'
                    : 'text-slate-700 hover:bg-cream-alt'
                }`}
              >
                <span
                  className={`font-inter text-[11px] font-semibold ${
                    selectedKey === s.key ? 'text-white' : 'text-slate-800'
                  }`}
                >
                  {s.label}
                </span>
                <span
                  className={`font-dank-mono text-[9px] ${
                    selectedKey === s.key ? 'text-white/70' : 'text-slate-400'
                  }`}
                >
                  {s.count}
                </span>
              </button>
            ))}
          </div>

          {/* ── Right asset grid ───────────────────────────────────── */}
          <div className="flex min-w-0 flex-1 flex-col">
            {selectedSection && (
              <p className="mb-2 font-dank-mono text-[9px] uppercase tracking-widest text-slate-500">
                {selectedSection.label}
                {filtered.length > 0 && (
                  <span className="ml-1.5 text-slate-400">{filtered.length}</span>
                )}
              </p>
            )}

            <div className="max-h-[48vh] overflow-y-auto">
              {loadingAssets ? (
                <div className="py-10 text-center font-inter text-xs text-slate-400">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="py-10 text-center font-inter text-xs text-slate-400">
                  {search ? 'No matches.' : 'No assets here yet.'}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {filtered.map((item) => (
                    <AssetThumb key={item.path} item={item} onPick={onPick} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload */}
      {showUploadButton && (
        <div className="flex items-center justify-end border-t border-beige pt-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-full bg-marine-green px-3 py-1.5 font-inter text-xs font-semibold text-white hover:bg-marine-green/90 disabled:opacity-60"
          >
            {uploading ? 'Uploading…' : 'Upload image'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }}
          />
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function SidebarBrandItem({
  section,
  active,
  onSelect,
}: {
  section: Section;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex items-center gap-2 rounded-lg p-1.5 text-left transition ${
        active ? 'bg-marine-green' : 'hover:bg-cream-alt'
      }`}
    >
      {/* Square cover thumbnail */}
      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md bg-slate-100">
        {section.cover ? (
          <img src={section.cover} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-dank-mono text-[10px] text-slate-400">
            ?
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p
          className={`truncate font-inter text-[11px] font-semibold leading-tight ${
            active ? 'text-white' : 'text-slate-800'
          }`}
        >
          {section.label}
        </p>
        <p className={`font-dank-mono text-[9px] ${active ? 'text-white/70' : 'text-slate-400'}`}>
          {section.count}
        </p>
      </div>
    </button>
  );
}

function AssetThumb({ item, onPick }: { item: AnyAsset; onPick: (path: string) => void }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <button
      type="button"
      onClick={() => onPick(item.path)}
      className="group relative block aspect-square overflow-hidden rounded-lg border border-beige bg-cream-alt transition hover:border-marine-green hover:shadow-sm"
    >
      {!error ? (
        <img
          src={item.path}
          alt={item.filename}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          className={`h-full w-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center font-dank-mono text-[9px] text-slate-300">
          err
        </div>
      )}
      {/* Filename tooltip on hover */}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-0 truncate bg-black/60 px-1.5 py-0.5 font-dank-mono text-[8px] text-white opacity-0 transition-opacity group-hover:opacity-100">
        {item.filename}
      </span>
    </button>
  );
}
